import { useEffect, useMemo, useState, useRef } from "react";

import { ensureCsrfToken, getLegacyUrl } from "../../api";

import { openInvoiceData } from "../../utils/invoiceDisplay";

import { openInvoicePdf } from "../../utils/openPdf";

import Swal from "sweetalert2";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function getPendingDuration(createdAt) {
  if (!createdAt) {
    return "-";
  }

  const createdTime = new Date(createdAt);
  const now = new Date();
  let diffMs = now - createdTime;
  if (diffMs < 0) {
    diffMs = 0;
  }

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

function StatusModal({ detail, onClose }) {
  if (!detail) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nomigo-status-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Status Detail</p>
            <h2 id="nomigo-status-title">{detail.label}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-note">{detail.message || "No additional message available."}</p>
        <pre className="processor-modal__code">
          {JSON.stringify(detail.data ?? {}, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function TrailModal({ trail, loading, onClose }) {
  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}



export default function ProcessorNoMigoView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusDetail, setStatusDetail] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);

  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [editForm, setEditForm] = useState({
    InvoiceGroupKey: "",
    InvoiceNo: "",
    PoNumber: "",
    GrnNumber: "",
    VendorGst: "",
    VendorCode: "",
    VendorName: "",
  });

  const [vendorVerification, setVendorVerification] = useState({
    status: "", // success | error
    message: "",
  });

  const [menuPosition, setMenuPosition] = useState(null);

  const summary = useMemo(
    () => ({
      count: rows.length,
      selected: selectedRows.length,
      redFlags: rows.reduce(
        (count, row) =>
          count + Object.values(row.InvoiceCheck || {}).filter((item) => item?.color === "r").length,
        0,
      ),
    }),
    [rows, selectedRows],
  );

  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuPosition) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuPosition]);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-invoices-with-nogrndata/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Unable to load invoices. Backend responded with ${response.status}.`,
        );
      }

      if (!payload) {
        throw new Error("Unable to load invoices. Backend returned a non-JSON response.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  function toggleRowSelection(invoiceGroupKey) {
    setSelectedRows((current) =>
      current.includes(invoiceGroupKey)
        ? current.filter((item) => item !== invoiceGroupKey)
        : [...current, invoiceGroupKey],
    );
  }

  function toggleAllRows() {
    setSelectedRows((current) => (current.length === rows.length ? [] : rows.map((row) => row.InvoiceGroupKey)));
  }

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.InvoiceId}/`, {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load trail.");
      }
      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  async function handleAction(row, type) {
    setStatus("");
    setError("");
    setActionKey(`${type}-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      } else if (type === "delete") {
        url = "/api/delete-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey, tab: "nomigodatagtab" };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}d successfully.`);
      setSelectedRows((current) => current.filter((item) => item !== row.InvoiceGroupKey));
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  async function handlePreview(row) {
    setStatus("");
    setError("");
    setActionKey(`preview-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/template-preview/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_keys: [row.InvoiceGroupKey],
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to generate preview.");
      }

      const previewWindow = window.open("", "_blank", "noreferrer");
      if (!previewWindow) {
        throw new Error("Preview window was blocked by the browser.");
      }

      let html = "<html><head><title>Preview</title>";
      html += "</head><body style='padding:24px;font-family:Segoe UI,sans-serif;'>";
      Object.entries(payload.template_html || {}).forEach(([key, value]) => {
        html += `<h3>${key}</h3>${value}<hr/>`;
      });
      html += "</body></html>";

      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (requestError) {
      setError(requestError.message || "Unable to generate preview.");
    } finally {
      setActionKey("");
    }
  }

  const openEditModal = async (groupKey) => {
    try {
      setEditLoading(true);
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-invoice-details/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          InvoiceGroupKey: groupKey,
          Tab: "no_data",
        }),
      });

      const result = await response.json();

      if (!result.success) {
        alert(result.message || "Unable to fetch invoice details");
        return;
      }

      setEditForm(result.data);

      setVendorVerification({
        status: "",
        message: "",
      });

      setShowEditModal(true);
    } catch (error) {
      console.error(error);
      alert("Error fetching invoice details");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const verifyVendorGst = async () => {
    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/lookup-vendor-by-gst/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          gst_number: editForm.VendorGst,
        }),
      });

      const result = await response.json();

      if (result.found) {
        setEditForm((prev) => ({
          ...prev,
          VendorCode: result.vendor_code,
          VendorName: result.vendor_name,
        }));

        setVendorVerification({
          status: "success",
          message: "Vendor found",
        });
      } else {
        setEditForm((prev) => ({
          ...prev,
          VendorCode: "",
          VendorName: "",
        }));

        setVendorVerification({
          status: "error",
          message: "No vendor found for entered GST number",
        });
      }
    } catch (err) {
      console.error(err);

      setVendorVerification({
        status: "error",
        message: "Vendor verification failed",
      });
    }
  };

  const submitEditedInvoice = async () => {
    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/submit-nomigo-invoice/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(editForm),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || "Invoice submitted successfully",
          confirmButtonText: "OK",
        });        
      } else {
        setShowEditModal(false);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: result.message || "Submission failed",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error(error);
      setShowEditModal(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to submit invoice",
        confirmButtonText: "OK",
      });
    }
  };

  function refreshNoMigo() {
    loadRows();
  }

  return (
    <section className="processor-view">
      <div className="workspace-panel processor-view__hero">
        <div>
          <p className="eyebrow">Processor</p>
          <h2>Not Found In Migo/Duplicate</h2>
          
        </div>

        <div className="processor-view__hero-actions">
          <div className="processor-summary-card">
            <strong>{summary.count}</strong>
            <span>invoices in queue</span>
          </div>
          <button
            className="ghost-button processor-view__refresh"
            type="button"
            onClick={refreshNoMigo}
          >
            Refresh Data
          </button>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      

      <div className="workspace-panel">
        

        {loading ? (
          <p className="config-note">Loading no-MIGO invoices…</p>
        ) : (
          <div className="config-table processor-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Vendor Name</th>
                  <th>Invoice No.</th>
                  <th>Invoice Date</th>
                  <th>Invoice Value</th>
                  <th>Reason</th>
                  <th>Pending Time</th>
                  <th>Status</th>
                  <th>Response</th>
                  <th>File</th>
                  <th>Preview</th>
                  <th>Logs</th>
                  <th>
                    Select
                    <input
                      className="processor-checkbox"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAllRows}
                    />
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((row) => (
                    <tr key={row.InvoiceGroupKey}>
                      <td>{row.InvoiceId}</td>
                      <td>{row.VendorName || "-"}</td>
                      <td>{row.InvNo || "-"}</td>
                      <td>{row.InvDate || "-"}</td>
                      <td>{formatAmount(row.TotalAmount)}</td>
                      <td>{row.message || "-"}</td>
                      <td>{getPendingDuration(row.created_at)}</td>
                      <td>
                        <div className="processor-status-list">
                          {Object.entries(row.InvoiceCheck || {}).length ? (
                            Object.entries(row.InvoiceCheck || {}).map(([label, item]) => (
                              <button
                                key={`${row.InvoiceGroupKey}-${label}`}
                                className={`processor-status-dot processor-status-dot--${item?.color || "o"}`}
                                type="button"
                                title={`${label}: ${item?.message || ""}`}
                                onClick={() =>
                                  setStatusDetail({
                                    label,
                                    message: item?.message,
                                    data: item?.data,
                                  })
                                }
                              />
                            ))
                          ) : (
                            <span className="empty-state">No checks</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {row.unique_name ? (
                          <button
                            onClick={() =>
                              openInvoiceData(
                                `${row.unique_name}.json`
                              )
                            }
                          >
                            Data
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {row.unique_name ? (
                          <button
                            type="button"
                            onClick={() => openInvoicePdf(row.InvoiceId)}
                          >
                            📎
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <button
                          className="ghost-button"
                          type="button"
                          disabled={actionKey === `preview-${row.InvoiceGroupKey}`}
                          onClick={() => handlePreview(row)}
                        >
                          {actionKey === `preview-${row.InvoiceGroupKey}` ? "Previewing..." : "Preview"}
                        </button>
                      </td>
                      <td>
                        <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                          Logs
                        </button>
                      </td>
                      <td>
                        <input
                          className="processor-checkbox"
                          type="checkbox"
                          checked={selectedRows.includes(row.InvoiceGroupKey)}
                          onChange={() => toggleRowSelection(row.InvoiceGroupKey)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="action-menu-button"
                          onClick={(e) => {

                            if (
                              menuPosition?.row?.InvoiceGroupKey === row.InvoiceGroupKey
                            ) {
                              setMenuPosition(null);
                              return;
                            }

                            const rect = e.currentTarget.getBoundingClientRect();

                            setMenuPosition({
                              row,
                              top: rect.bottom + 5,
                              left: rect.left,
                            });
                          }}
                        >
                          Actions ▼
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14">No invoices found in the no-MIGO or duplicate queue.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showEditModal && (
        <div className="nomigo-edit-modal-overlay">
          <div className="nomigo-edit-modal">
            <div className="nomigo-edit-modal-header">
              <h2>Edit Invoice</h2>

              <button
                type="button"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>

            {/* Section 1 */}

            <div className="nomigo-edit-modal-section">
              <h3>Invoice Information</h3>

              <div className="nomigo-edit-form-grid">

                <div className="nomigo-edit-field">
                  <label>Invoice Number</label>
                  <input
                    type="text"
                    name="InvoiceNo"
                    value={editForm.InvoiceNo || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>PO Number</label>
                  <input
                    type="text"
                    name="PoNumber"
                    value={editForm.PoNumber || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>GRN Number</label>
                  <input
                    type="text"
                    name="GrnNumber"
                    value={editForm.GrnNumber || ""}
                    onChange={handleEditChange}
                  />
                </div>

              </div>
            </div>

            {/* Section 2 */}

            <div className="nomigo-edit-modal-section">
              <h3>Vendor Verification</h3>

              <div className="nomigo-edit-form-grid">

                <div className="nomigo-edit-field">
                  <label>Vendor GST</label>
                  <input
                    type="text"
                    name="VendorGst"
                    value={editForm.VendorGst || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-verify-wrapper">
                  <button
                    type="button"
                    className="nomigo-edit-verify-btn"
                    onClick={verifyVendorGst}
                  >
                    Verify Vendor
                  </button>
                </div>

                <div className="nomigo-edit-field">
                  <label>Vendor Code</label>
                  <input
                    type="text"
                    value={editForm.VendorCode || ""}
                    readOnly
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>Vendor Name</label>
                  <input
                    type="text"
                    value={editForm.VendorName || ""}
                    readOnly
                  />
                </div>

              </div>
            </div>

            <div className="nomigo-edit-footer">
              <button
                type="button"
                className="primary-button"
                onClick={submitEditedInvoice}
              >
                Submit Invoice
              </button>
            </div>

          </div>
        </div>
      )}
      {menuPosition && (
        <div
          ref={menuRef}
          className="global-action-menu"
          style={{
            position: "fixed",
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 999999,
          }}
        >
          

          <button
            type="button"
            onClick={() => {
              openEditModal(menuPosition.row.InvoiceGroupKey);
              setMenuPosition(null);
            }}
          >
            ✎ Edit
          </button>

          <button
            type="button"
            disabled={
              actionKey ===
              `reject-${menuPosition.row.InvoiceGroupKey}`
            }
            onClick={() => {
              handleAction(menuPosition.row, "reject");
              setMenuPosition(null);
            }}
          >
            ✕ Reject
          </button>

          <button
            type="button"
            className="danger-item"
            disabled={
              actionKey ===
              `delete-${menuPosition.row.InvoiceGroupKey}`
            }
            onClick={() => {
              handleAction(menuPosition.row, "delete");
              setMenuPosition(null);
            }}
          >
            🗑 Delete
          </button>
        </div>
      )}
      <StatusModal detail={statusDetail} onClose={() => setStatusDetail(null)} />
      <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
    </section>
  );
}
